import { useState, useMemo, useEffect, type ReactNode } from 'react'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { useApi } from '../hooks/useApi'
import { useData } from '../hooks/useData'
import { useAuth } from '../hooks/useAuth'
import LoadingSpinner from '../components/LoadingSpinner'
import ReportsTabs from '../components/ReportsTabs'
import type { BauSnapshot, JiraSnapshot } from '../types'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, Legend,
} from 'recharts'
import {
  AlertTriangle, TrendingDown, Clock, Layers, ShieldCheck,
  BarChart3, Activity, TableProperties, Target, CheckCircle2,
  Presentation, Database,
} from 'lucide-react'
import MultiSelect from '../components/MultiSelect'
import { ChartCard, Dot } from '../components/ChartCard'
import InfoTooltip from '../components/InfoTooltip'
import JiraPanel from '../components/JiraPanel'
import {
  type JiraFilter, EMPTY_JIRA_FILTER, prevMonthParts, availableYears, filterJiraIssues, MONTH_OPTIONS,
} from '../lib/jira'

const BLUE  = '#005EB8'
const CYAN  = '#00ACED'
const RED   = '#EF3340'
const AMBER = '#F88806'
const GREEN = '#10b981'
const TIP   = { fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,.08)' }

type TrendEntry = { month: string; total: number; breached: number }

const TEAMS: Record<string, {
  label: string; color: string
  allTimeTotal: number; currentMonth: number; lastMonth: number
  currentBreached: number; lastBreached: number
  openBacklog: number
  trend: TrendEntry[]
  serviceMonthly:          Record<string, Record<string, number>>
  priorityMonthly:         Record<string, Record<string, number>>
  typeMonthly:             Record<string, Record<string, number>>
  typeByState:             Record<string, Record<string, number>>
  incidentPriorityMonthly: Record<string, Record<string, number>>
  incidentSlaMonthly:      Record<string, { total: number; breached: number }>
}> = {}

const ALL_MONTHS: string[] = []

const TEAM_KEYS    = Object.keys(TEAMS)
const TEAM_OPTIONS = TEAM_KEYS.map(k => [k, TEAMS[k].label] as [string, string])

type Period = 'MTD' | '3M' | '6M' | 'YTD' | '12M'
const PERIOD_LABELS: Record<Period, string> = { MTD: 'MTD', '3M': '3M', '6M': '6M', YTD: 'YTD', '12M': '12M' }


const TOGGLES = [
  { key: 'rag',      label: 'Monthly Health',   cls: 'bg-[#10b981]/10 border-[#10b981]/25 text-[#059669]'  },
  { key: 'trend',    label: 'Ticket Volume',     cls: 'bg-[#005EB8]/5 border-[#005EB8]/20 text-[#005EB8]'  },
  { key: 'services', label: 'By IT Service',     cls: 'bg-[#00ACED]/10 border-[#00ACED]/25 text-[#0077A8]' },
  { key: 'breach',   label: 'Breach Rate',       cls: 'bg-[#EF3340]/10 border-[#EF3340]/25 text-[#B8242E]' },
  { key: 'types',    label: 'By Ticket Type',    cls: 'bg-slate-50 border-slate-200 text-slate-800'        },
  { key: 'teams',    label: 'Team Breakdown',    cls: 'bg-[#002677]/5 border-[#002677]/20 text-[#002677]'  },
] as const

// Data-source selector for the Operational page. Single-select: ServiceNow and
// Jira are viewed one at a time. Their metrics don't share a taxonomy
// (Question/Change/Incident vs Task/Bug/Story, SLA vs none, IT Service vs
// Product), so merging them into shared charts would mislead - each gets its
// own purpose-built view instead.
const SOURCE_OPTIONS = [
  { key: 'servicenow', label: 'ServiceNow' },
  { key: 'jira',       label: 'Jira (Commercial AEM BAU)' },
] as const

const PRI_COLOR: Record<string, string> = { P1: RED, P2: '#F97316', P3: AMBER, P4: BLUE, P5: '#94a3b8' }
const PRI_LABEL: Record<string, string> = { P1: 'Critical', P2: 'High', P3: 'Moderate', P4: 'Low', P5: 'Planning' }
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const fmtM = (m: string) => { const [y, mo] = m.split('-'); return `${MONTH_NAMES[parseInt(mo)-1]} ${y.slice(2)}` }

function StatTile({ icon: Icon, iconBg, label, value, bar, barColor, sub }: {
  icon: React.ElementType; iconBg: string; label: string
  value: string | number; bar?: number; barColor?: string; sub?: string
}) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: iconBg }}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-0.5">{label}</div>
        <div className="text-2xl font-bold text-slate-800 leading-none">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
        {bar !== undefined && (
          <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.min(bar, 100)}%`, background: barColor ?? BLUE }} />
          </div>
        )}
      </div>
    </div>
  )
}

function BauEmpty({ api }: { api: ReturnType<typeof useApi> }) {
  const { user } = useAuth()
  const isAdmin = !!user?.isAdmin
  const [syncing, setSyncing] = useState(false)
  const [done, setDone] = useState(false)

  const handleSync = async () => {
    setSyncing(true)
    try {
      await api.triggerBauSync()
      setDone(true)
    } finally {
      setSyncing(false)
    }
  }

  return (
    <div>
      <ReportsTabs active="bau" />
      <h1 className="text-2xl font-bold text-slate-800 mb-1">Operational BAU</h1>
      <p className="text-slate-500 text-sm mb-8">ServiceNow ticket activity by team</p>
      <div className="bg-white rounded-2xl border border-slate-200 p-12 flex flex-col items-center justify-center text-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
          <Activity className="w-7 h-7 text-slate-400" />
        </div>
        <div>
          <div className="text-slate-700 font-semibold mb-1">No data yet</div>
          <div className="text-slate-400 text-sm max-w-sm">
            The daily sync runs at 09:00 UTC. {isAdmin ? 'You can trigger it manually below.' : 'Check back after 09:00 UTC.'}
          </div>
        </div>
        {isAdmin && (
          done
            ? <div className="text-sm text-green-600 font-medium">Sync triggered. Data will appear in a few minutes. Refresh the page.</div>
            : <button onClick={handleSync} disabled={syncing}
                className="flex items-center gap-2 px-4 py-2 bg-[#005EB8] text-white rounded-lg text-sm font-semibold hover:bg-[#004a94] disabled:opacity-50 transition-colors">
                {syncing ? <><ShieldCheck className="w-4 h-4 animate-pulse" /> Syncing…</> : <><ShieldCheck className="w-4 h-4" /> Sync now</>}
              </button>
        )}
      </div>
    </div>
  )
}

export default function BAU() {
  useDocumentTitle('Operational BAU')
  const api = useApi()
  const { user } = useAuth()
  const isAdmin = !!user?.isAdmin
  // Fetch both operational sources up front. Each catches independently so a
  // 404 on one (never synced) doesn't strand the other.
  const { data, loading } = useData(() =>
    Promise.all([
      (api.getBauSnapshot() as Promise<BauSnapshot | null>).catch(() => null),
      (api.getJiraSnapshot() as Promise<JiraSnapshot | null>).catch(() => null),
    ]).then(([bau, jira]) => ({ bau, jira })),
  )
  const snapshot = data?.bau ?? null
  const jiraSnapshot = data?.jira ?? null

  const [sources, setSources] = useState<string[]>(['servicenow'])
  const snActive = sources.includes('servicenow')
  const jiraActive = sources.includes('jira')
  const selectSource = (key: string) => setSources([key])
  // If ServiceNow has never synced but Jira has, open on Jira so the page isn't blank.
  useEffect(() => {
    if (data && !data.bau && data.jira) setSources(['jira'])
  }, [data])

  // Jira filter state lives here (page level) so its controls can sit in the
  // shared data-source box, mirroring the ServiceNow controls, while JiraPanel
  // renders the content below. Defaults to the previous month.
  const [jiraFilter, setJiraFilter] = useState<JiraFilter>(() => ({ ...EMPTY_JIRA_FILTER, ...prevMonthParts() }))
  const jiraYears = useMemo(() => (jiraSnapshot ? availableYears(jiraSnapshot.issues) : []), [jiraSnapshot])
  const jiraFiltered = useMemo(
    () => (jiraSnapshot ? filterJiraIssues(jiraSnapshot.issues, jiraFilter) : []),
    [jiraSnapshot, jiraFilter],
  )
  const jiraUpdate = (patch: Partial<JiraFilter>) => setJiraFilter(f => ({ ...f, ...patch }))
  const jiraClear = () => setJiraFilter({ ...EMPTY_JIRA_FILTER })
  const jiraToggleLabel = (label: string) =>
    setJiraFilter(f => ({
      ...f,
      labels: f.labels.includes(label) ? f.labels.filter(l => l !== label) : [...f.labels, label],
    }))
  const jiraHasFilter =
    jiraFilter.years.length > 0 || jiraFilter.months.length > 0 || jiraFilter.statuses.length > 0 ||
    jiraFilter.types.length > 0 || jiraFilter.labels.length > 0 || jiraFilter.priorities.length > 0

  // When using live snapshot, group keys come from byAssignmentGroup; otherwise from TEAMS
  const liveGroupKeys = useMemo(
    () => snapshot?.byAssignmentGroup?.map(g => g.group) ?? [],
    [snapshot]
  )
  const effectiveKeys = TEAM_KEYS.length > 0 ? TEAM_KEYS : liveGroupKeys
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [period, setPeriod] = useState<Period>('3M')
  const [visible, setVisible] = useState<Record<string, boolean>>(
    () => Object.fromEntries(TOGGLES.map(t => [t.key, true]))
  )
  const toggleSection = (key: string) => setVisible(v => ({ ...v, [key]: !v[key] }))
  const [presenting, setPresenting] = useState(false)

  // In snapshot mode ALL_MONTHS is empty - derive available months from snapshot.monthlyTrend
  // Single derivation - all filtering happens here, render just reads results
  const derived = useMemo(() => {
    const curYear = new Date().getFullYear()
    const src = snapshot  // may be null; helpers below guard with ?? []

    const allMths = src?.monthlyTrend?.map(m => m.month).sort() ?? ALL_MONTHS
    const periodMths = (() => {
      if (!allMths.length) return []
      switch (period) {
        case 'MTD': return allMths.slice(-1)
        case '3M':  return allMths.slice(-3)
        case '6M':  return allMths.slice(-6)
        case 'YTD': return allMths.filter(m => m >= `${curYear}-01`)
        case '12M': return allMths.slice(-12)
      }
    })()
    const chartMths = period === 'MTD' ? allMths.slice(-3) : periodMths
    const pMonthSet = new Set(periodMths)
    const activeGroups = selectedKeys.length > 0 ? new Set(selectedKeys) : null

    // ── Ticket trend (period + group filtered) ──────────────────────────────
    let trend: { month: string; total: number; breached: number; met: number; label: string }[]
    if (activeGroups && src?.groupMonthlyTrend?.length) {
      const byMonth = new Map<string, { total: number; breached: number }>()
      for (const e of src.groupMonthlyTrend) {
        if (!activeGroups.has(e.group) || !pMonthSet.has(e.month)) continue
        const c = byMonth.get(e.month) ?? { total: 0, breached: 0 }
        byMonth.set(e.month, { total: c.total + e.total, breached: c.breached + e.breached })
      }
      trend = periodMths.map(m => {
        const v = byMonth.get(m) ?? { total: 0, breached: 0 }
        return { month: m, ...v, met: v.total - v.breached, label: fmtM(m) }
      })
    } else {
      trend = periodMths.map(m => {
        const s = src?.monthlyTrend?.find(x => x.month === m)
        const t = s?.total ?? 0, b = s?.breached ?? 0
        return { month: m, total: t, breached: b, met: t - b, label: fmtM(m) }
      })
    }
    const total    = trend.reduce((s, m) => s + m.total, 0)
    const breached = trend.reduce((s, m) => s + m.breached, 0)

    // ── Incident data - use per-group data when groups selected, else all-groups ──
    const incPriSource = activeGroups && src?.groupIncidentPriority?.length
      ? src.groupIncidentPriority.filter(e => activeGroups.has(e.group))
      : src?.incidentByMonthPriority ?? []
    const incPriMap = new Map<string, { p1: number; p2: number }>()
    for (const e of incPriSource) {
      if (!pMonthSet.has(e.month)) continue
      const c = incPriMap.get(e.month) ?? { p1: 0, p2: 0 }
      if (e.priority === 'P1' || e.priority === '1') c.p1 += e.count
      if (e.priority === 'P2' || e.priority === '2') c.p2 += e.count
      incPriMap.set(e.month, c)
    }
    const incSlaSource = activeGroups && src?.groupIncidentSla?.length
      ? src.groupIncidentSla.filter(e => activeGroups.has(e.group))
      : src?.incidentSlaByMonth ?? []
    const slaByMonth = new Map<string, { total: number; breached: number }>()
    for (const e of incSlaSource) {
      if (!pMonthSet.has(e.month)) continue
      const cur = slaByMonth.get(e.month) ?? { total: 0, breached: 0 }
      slaByMonth.set(e.month, { total: cur.total + e.total, breached: cur.breached + e.breached })
    }
    const slaMap = slaByMonth

    let p1 = 0, p2 = 0, incTotal = 0, incBreached = 0
    for (const m of periodMths) {
      const pri = incPriMap.get(m) ?? { p1: 0, p2: 0 }
      p1 += pri.p1; p2 += pri.p2
      const sla = slaMap.get(m)
      if (sla) { incTotal += sla.total; incBreached += sla.breached }
    }
    const slaRate = incTotal > 0 ? Math.round(((incTotal - incBreached) / incTotal) * 100) : 100

    const monthRag = periodMths.map(month => {
      const pri = incPriMap.get(month) ?? { p1: 0, p2: 0 }
      const sla = slaMap.get(month)
      const mT = sla?.total ?? 0, mB = sla?.breached ?? 0
      const mSla = mT > 0 ? Math.round(((mT - mB) / mT) * 100) : 100
      return { month, p1: pri.p1, p2: pri.p2, incTotal: mT, incBreached: mB, slaRate: mSla,
               green: pri.p1 === 0 && pri.p2 < 4 && mSla >= 90 }
    })

    // ── Per-group charts (top 5 or selected) ────────────────────────────────
    const dispGroups = activeGroups ? [...activeGroups] : liveGroupKeys.slice(0, 5)
    const grpTrend = chartMths.map(month => {
      const row: Record<string, any> = { label: fmtM(month) }
      for (const grp of dispGroups) {
        const e = src?.groupMonthlyTrend?.find(x => x.group === grp && x.month === month)
        row[grp] = e?.total ?? 0
      }
      return row
    })
    const grpBreach = chartMths.map(month => {
      const row: Record<string, any> = { label: fmtM(month) }
      for (const grp of dispGroups) {
        const e = src?.groupMonthlyTrend?.find(x => x.group === grp && x.month === month)
        const t = e?.total ?? 0
        row[grp] = t > 0 ? Math.round(((e?.breached ?? 0) / t) * 100) : 0
      }
      return row
    })
    const grpPeriod = dispGroups.map(grp => {
      const entries = (src?.groupMonthlyTrend ?? []).filter(e => e.group === grp && pMonthSet.has(e.month))
      const gT = entries.reduce((s, e) => s + e.total, 0)
      const gB = entries.reduce((s, e) => s + e.breached, 0)
      return { key: grp, label: grp, color: BLUE, total: gT, breached: gB,
               rate: gT > 0 ? Math.round((gB / gT) * 100) : 0 }
    })

    // ── By Service + By Type (period + group filtered) ───────────────────────
    const svcMap = new Map<string, number>()
    const typMap = new Map<string, number>()
    for (const e of src?.groupByService ?? []) {
      if (!pMonthSet.has(e.month)) continue
      if (activeGroups && !activeGroups.has(e.group)) continue
      svcMap.set(e.service, (svcMap.get(e.service) ?? 0) + e.count)
    }
    for (const e of src?.groupByType ?? []) {
      if (!pMonthSet.has(e.month)) continue
      if (activeGroups && !activeGroups.has(e.group)) continue
      typMap.set(e.type, (typMap.get(e.type) ?? 0) + e.count)
    }
    const services = svcMap.size > 0
      ? [...svcMap.entries()].map(([service, count]) => ({ service, count })).sort((a, b) => b.count - a.count)
      : src?.byService ?? []
    const types = typMap.size > 0
      ? Object.fromEntries([...typMap.entries()])
      : Object.fromEntries((src?.byType ?? []).map(t => [t.type, t.count]))

    return {
      periodMonths: periodMths, chartMonths: chartMths,
      trend, total, breached, rate: total > 0 ? Math.round((breached / total) * 100) : 0,
      incidentSummary: { p1, p2, incTotal, incBreached, slaRate },
      monthRag, grpTrend, grpBreach, grpPeriod, services, types,
    }
  }, [snapshot, period, selectedKeys, liveGroupKeys])

  // Aliases so the render below doesn't need changing
  const incidentSummary = derived?.incidentSummary ?? { p1: 0, p2: 0, incTotal: 0, incBreached: 0, slaRate: 100 }
  const monthRag        = derived?.monthRag       ?? []
  const combinedTrend   = derived?.trend          ?? []
  const perTeamTrend    = derived?.grpTrend       ?? []
  const breachRateTrend = derived?.grpBreach      ?? []
  const perTeamPeriod   = derived?.grpPeriod      ?? []
  const periodTotals    = { total: derived?.total ?? 0, breached: derived?.breached ?? 0, rate: derived?.rate ?? 0 }
  const aggServices = derived?.services ?? snapshot?.byService ?? []
  const aggTypes    = derived?.types    ?? Object.fromEntries((snapshot?.byType ?? []).map(t => [t.type, t.count]))
  const aggByStatus: Record<string, number> = {}
  const aggPriority: [string, number][]     = []

  const maxService  = aggServices[0]?.count ?? 1
  const maxPriority = aggPriority.length > 0 ? Math.max(...aggPriority.map(([, c]) => c)) : 1
  const incSlaColor = incidentSummary.slaRate >= 90 ? GREEN : incidentSummary.slaRate >= 75 ? AMBER : RED
  const p2Color     = incidentSummary.p2 === 0 ? GREEN : incidentSummary.p2 < 4 ? AMBER : RED

  // ServiceNow summary tiles (incident-focused). Jira is a separate, self-
  // contained view (JiraPanel), so these are ServiceNow-only.
  const tiles: {
    label: string; value: string | number; icon: React.ElementType; iconBg: string
    bar?: number; barColor?: string; sub?: string
  }[] = [
    {
      label: 'P1 Incidents', value: incidentSummary.p1, icon: AlertTriangle,
      iconBg: incidentSummary.p1 > 0 ? RED : GREEN, bar: incidentSummary.p1 > 0 ? 100 : 0, barColor: RED,
      sub: incidentSummary.p1 === 0 ? 'None in period' : undefined,
    },
    {
      label: 'P2 Incidents', value: incidentSummary.p2, icon: AlertTriangle, iconBg: p2Color,
      bar: incidentSummary.p2 > 0 ? Math.min((incidentSummary.p2 / 4) * 100, 100) : 0, barColor: p2Color,
      sub: incidentSummary.p2 === 0 ? 'None in period' : incidentSummary.p2 < 4 ? 'Below threshold (<4)' : '>=4 threshold exceeded',
    },
    {
      label: 'Incident SLA', value: incidentSummary.incTotal > 0 ? `${incidentSummary.slaRate}%` : '- ',
      icon: ShieldCheck, iconBg: incidentSummary.incTotal > 0 ? incSlaColor : GREEN,
      bar: incidentSummary.incTotal > 0 ? incidentSummary.slaRate : 100, barColor: incSlaColor,
      sub: incidentSummary.incTotal > 0
        ? `${incidentSummary.incTotal - incidentSummary.incBreached}/${incidentSummary.incTotal} met SLA`
        : 'No incidents in period',
    },
    {
      label: 'Total Tickets', value: periodTotals.total, icon: Layers, iconBg: BLUE,
      bar: 100 - periodTotals.rate, barColor: BLUE,
      sub: `${periodTotals.breached} SLA breaches (${periodTotals.rate}%)`,
    },
  ]

  if (presenting) {
    const top5Services = aggServices.slice(0, 6)
    const maxSvc = top5Services[0]?.count ?? 1
    return (
      <SlideFrame onExit={() => setPresenting(false)}>
        <div className="w-full h-full flex flex-col bg-white overflow-hidden">
          {/* 4px brand gradient bar */}
          <div className="h-1 w-full flex-shrink-0" style={{ background: 'linear-gradient(90deg,#005EB8 0%,#00ACED 50%,#54AD18 100%)' }} />

          {/* Header */}
          <div className="flex items-center justify-between px-7 pt-4 pb-3 flex-shrink-0">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#00ACED] mb-0.5">Operational BAU</p>
              <h1 className="text-xl font-bold text-[#002677] leading-none">UKIE IT Support · ServiceNow</h1>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-slate-400">
              <span className="font-semibold">{selectedKeys.length > 0 ? selectedKeys.join(' · ') : 'All groups'}</span>
              <span className="bg-slate-100 rounded-md px-2 py-1 font-bold text-slate-500">{PERIOD_LABELS[period]}</span>
            </div>
          </div>

          {/* 4 KPI tiles */}
          <div className="grid grid-cols-4 gap-3 px-7 flex-shrink-0">
            {[
              { label: 'P1 Incidents',   value: incidentSummary.p1,   sub: incidentSummary.p1 === 0 ? 'None in period' : 'Critical', color: incidentSummary.p1 > 0 ? RED : GREEN },
              { label: 'P2 Incidents',   value: incidentSummary.p2,   sub: incidentSummary.p2 === 0 ? 'None in period' : incidentSummary.p2 < 4 ? 'Below threshold' : '≥4 exceeded', color: p2Color },
              { label: 'Incident SLA',   value: incidentSummary.incTotal > 0 ? `${incidentSummary.slaRate}%` : '- ', sub: incidentSummary.incTotal > 0 ? `${incidentSummary.incTotal - incidentSummary.incBreached}/${incidentSummary.incTotal} met` : 'No incidents', color: incidentSummary.incTotal > 0 ? incSlaColor : GREEN },
              { label: 'Total Tickets',  value: periodTotals.total,   sub: `${periodTotals.breached} breaches · ${periodTotals.rate}% rate`, color: BLUE },
            ].map(({ label, value, sub, color }) => (
              <div key={label} className="bg-slate-50 rounded-xl border border-slate-100 px-4 py-2.5 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: color }}>
                  <ShieldCheck className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="text-[9px] font-bold uppercase tracking-wide text-slate-400 leading-none">{label}</div>
                  <div className="text-xl font-extrabold text-slate-800 leading-tight mt-0.5">{typeof value === 'number' ? value.toLocaleString() : value}</div>
                  <div className="text-[9px] text-slate-400 leading-none">{sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* RAG strip */}
          <div className="px-7 mt-3 flex-shrink-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Monthly Incident Health</span>
              <span className="text-[9px] text-slate-400">· Green = no P1 · &lt;4 P2s · ≥90% SLA</span>
            </div>
            <div className="flex rounded-xl overflow-hidden border border-slate-200">
              {monthRag.map(({ month, green }, i) => (
                <div key={month} className={`flex-1 flex flex-col items-center justify-center py-2 ${i > 0 ? 'border-l border-slate-200' : ''} ${green ? 'bg-green-50' : 'bg-red-50'}`}>
                  <span className={`text-[10px] font-bold ${green ? 'text-green-700' : 'text-red-700'}`}>{fmtM(month)}</span>
                  <span className={`text-xs font-bold leading-none mt-0.5 ${green ? 'text-green-500' : 'text-red-500'}`}>{green ? '✓' : '✗'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-2 gap-3 px-7 mt-3 flex-1 min-h-0">
            {/* Ticket Volume */}
            <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col min-h-0">
              <div className="flex items-center justify-between mb-1 flex-shrink-0">
                <h3 className="text-[10px] font-bold text-[#002677] uppercase tracking-wide">Ticket Volume</h3>
                <span className="text-[9px] text-slate-400">{periodTotals.total} tickets · {periodTotals.rate}% breach</span>
              </div>
              <div className="flex-1 min-h-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={combinedTrend} margin={{ left: 0, right: 4, top: 2, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#94a3b8' }} height={16} />
                    <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} width={24} domain={[0, 'dataMax + 2']} allowDecimals={false} />
                    <Tooltip contentStyle={TIP} formatter={(v: any, n: any) => [v, n === 'met' ? 'Met SLA' : 'Breached']} />
                    <Bar dataKey="met"      stackId="s" fill={`${BLUE}90`} name="Met SLA"  radius={[0,0,0,0]} />
                    <Bar dataKey="breached" stackId="s" fill={RED}          name="Breached" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* By IT Service */}
            <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col min-h-0">
              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                <h3 className="text-[10px] font-bold text-[#002677] uppercase tracking-wide">By IT Service</h3>
                <span className="text-[9px] text-slate-400">Top {top5Services.length}</span>
              </div>
              <div className="space-y-2 flex-1 overflow-hidden">
                {top5Services.map(({ service, count }) => (
                  <div key={service}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[10px] font-medium text-slate-700 truncate max-w-[180px]">{service}</span>
                      <span className="text-[10px] font-bold text-slate-600 ml-1 flex-shrink-0">{count}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100">
                      <div className="h-1.5 rounded-full" style={{ width: `${(count / maxSvc) * 100}%`, background: BLUE }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </SlideFrame>
    )
  }

  if (loading) return <LoadingSpinner />

  // Only the true "nothing to show" case - neither source has ever synced.
  if (!snapshot && !jiraSnapshot) {
    return <BauEmpty api={api} />
  }

  return (
    <div className="space-y-4 pb-10">
      <ReportsTabs active="bau" />

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          {jiraActive ? (
            <>
              <h1 className="text-2xl font-bold text-slate-800">Jira - Commercial AEM BAU</h1>
              <p className="text-sm text-slate-400 mt-0.5">
                {jiraSnapshot
                  ? `Project ${jiraSnapshot.projectKey} · ${jiraSnapshot.storedCount.toLocaleString()} issues since 2024`
                  : 'Operational reporting'}
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-slate-800">Operational BAU</h1>
              <p className="text-sm text-slate-400 mt-0.5">ServiceNow</p>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {snActive && snapshot && (
            <button onClick={() => setPresenting(true)}
              className="px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm border transition-colors bg-white text-slate-700 border-slate-200 hover:bg-slate-50">
              <Presentation className="w-4 h-4" /> Present
            </button>
          )}
          {snActive && snapshot?.syncedAt && (
            <div className="flex items-center gap-1.5 text-xs text-slate-400 bg-white border border-slate-200 rounded-lg px-3 py-2 shadow-sm">
              <Clock className="w-3.5 h-3.5" />Synced {new Date(snapshot.syncedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
          {jiraActive && jiraSnapshot?.syncedAt && (
            <div className="flex items-center gap-1.5 text-xs text-slate-400 bg-white border border-slate-200 rounded-lg px-3 py-2 shadow-sm">
              <Clock className="w-3.5 h-3.5" />Synced {new Date(jiraSnapshot.syncedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-4 items-start">
        {/* Data source + period */}
        <div className="flex items-center gap-4 flex-wrap w-full">
          <div className="flex items-center gap-2 flex-wrap flex-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Data source</span>
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
              {SOURCE_OPTIONS.map(o => (
                <button key={o.key} onClick={() => selectSource(o.key)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                    sources[0] === o.key ? 'bg-white text-[#005EB8] shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}>{o.label}</button>
              ))}
            </div>
          </div>
          {snActive && (
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
              {(Object.keys(PERIOD_LABELS) as Period[]).map(p => (
                <button key={p} onClick={() => setPeriod(p)}
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                    period === p ? 'bg-white text-[#005EB8] shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}>{PERIOD_LABELS[p]}</button>
              ))}
            </div>
          )}
        </div>

        {/* ServiceNow-specific controls */}
        {snActive && (
          <div className="border-t border-slate-100 w-full pt-3 mt-1 flex flex-col gap-3">
            {effectiveKeys.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">ServiceNow group</span>
                <MultiSelect
                  values={selectedKeys}
                  onChange={setSelectedKeys}
                  options={TEAM_KEYS.length > 0 ? TEAM_OPTIONS : effectiveKeys}
                  placeholder="Assignment Group"
                  searchable={effectiveKeys.length > 8}
                />
                {selectedKeys.length > 0 && selectedKeys.length < effectiveKeys.length && (
                  <button onClick={() => setSelectedKeys([])} className="text-sm text-[#00ACED] font-semibold hover:text-[#005EB8]">Clear</button>
                )}
              </div>
            )}
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Show sections:</div>
              <div className="flex flex-wrap gap-2">
                {TOGGLES.map(t => (
                  <label key={t.key} className={`flex items-center gap-2 rounded-lg px-3 py-1.5 cursor-pointer border select-none ${t.cls}`}>
                    <input type="checkbox" checked={visible[t.key] ?? true} onChange={() => toggleSection(t.key)}
                      className="w-3.5 h-3.5 rounded" style={{ accentColor: '#005EB8' }} />
                    <span className="text-xs font-medium">{t.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Jira-specific controls - same box, mirroring ServiceNow */}
        {jiraActive && jiraSnapshot && (
          <div className="border-t border-slate-100 w-full pt-3 mt-1 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Jira filters</span>
            <MultiSelect values={jiraFilter.years} onChange={v => jiraUpdate({ years: v })} options={jiraYears} placeholder="Year" searchable={jiraYears.length > 8} />
            <MultiSelect values={jiraFilter.months} onChange={v => jiraUpdate({ months: v })} options={MONTH_OPTIONS} placeholder="Month" />
            <MultiSelect values={jiraFilter.statuses} onChange={v => jiraUpdate({ statuses: v })} options={jiraSnapshot.statuses} placeholder="Status" searchable={jiraSnapshot.statuses.length > 8} />
            <MultiSelect values={jiraFilter.types} onChange={v => jiraUpdate({ types: v })} options={jiraSnapshot.types} placeholder="Type" />
            <MultiSelect values={jiraFilter.labels} onChange={v => jiraUpdate({ labels: v })} options={jiraSnapshot.labels} placeholder="Label" searchable />
            <MultiSelect values={jiraFilter.priorities} onChange={v => jiraUpdate({ priorities: v })} options={jiraSnapshot.priorities} placeholder="Priority" />
            {jiraHasFilter && (
              <button onClick={jiraClear} className="text-sm text-[#00ACED] font-semibold hover:text-[#005EB8]">Clear</button>
            )}
            <span className="ml-auto text-xs text-slate-400">{jiraFiltered.length.toLocaleString()} issue{jiraFiltered.length === 1 ? '' : 's'}</span>
          </div>
        )}
      </div>

      {/* ServiceNow incident summary tiles */}
      {snActive && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {tiles.map(t => (
            <StatTile key={t.label} icon={t.icon} iconBg={t.iconBg} label={t.label} value={t.value}
              bar={t.bar} barColor={t.barColor} sub={t.sub} />
          ))}
        </div>
      )}

      {snActive && (
        <div className="grid grid-cols-2 gap-4 grid-flow-row-dense">

          {/* Monthly Health RAG */}
          {visible.rag && (
            <div className="col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#10b981] to-[#059669] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <h3 className="font-bold text-slate-800">Monthly Incident Health</h3>
                    <InfoTooltip>
                      <strong>Green</strong> = no P1 incidents · fewer than 4 P2s · ≥90% of incidents resolved within SLA<br />
                      <strong>Red</strong> = any of those thresholds exceeded<br />
                      Hover each month for exact figures.
                    </InfoTooltip>
                  </div>
                  <p className="text-xs text-slate-400">Incident-level health check</p>
                </div>
              </div>
              <div className="flex rounded-xl overflow-hidden border border-slate-200">
                {monthRag.map(({ month, p1, p2, incTotal, incBreached, slaRate, green }, i) => (
                  <div key={month}
                    title={incTotal > 0
                      ? `P1: ${p1} | P2: ${p2} | SLA: ${slaRate}% (${incTotal - incBreached}/${incTotal} met)`
                      : 'No incidents this month'}
                    className={`flex-1 flex flex-col items-center justify-center py-3 cursor-default transition-opacity hover:opacity-75 ${
                      i > 0 ? 'border-l border-slate-200' : ''
                    } ${green ? 'bg-green-50' : 'bg-red-50'}`}
                  >
                    <span className={`text-[11px] font-bold ${green ? 'text-green-700' : 'text-red-700'}`}>{fmtM(month)}</span>
                    <span className={`text-sm font-bold leading-none mt-1 ${green ? 'text-green-500' : 'text-red-500'}`}>
                      {green ? '✓' : '✗'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ticket Volume */}
          {visible.trend && (
            <ChartCard icon={BarChart3} iconGrad="from-[#005EB8] to-[#005EB8] text-white"
              title="Ticket Volume" subtitle="Monthly breakdown"
              footer={selectedKeys.length <= 1
                ? <><Dot color="bg-[#005EB8]/55" label="Met SLA" /><Dot color="bg-[#EF3340]" label="Breached" /></>
                : undefined}
            >
              <div style={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  {selectedKeys.length <= 1 ? (
                    <BarChart data={combinedTrend} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} height={20} />
                      <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} width={28} domain={[0, 'dataMax + 2']} allowDecimals={false} />
                      <Tooltip contentStyle={TIP} formatter={(v: any, n: any) => [v, n === 'met' ? 'Met SLA' : 'SLA Breached']} />
                      <Bar dataKey="met"      stackId="s" fill={`${BLUE}90`} name="Met SLA"      radius={[0,0,0,0]} />
                      <Bar dataKey="breached" stackId="s" fill={RED}         name="SLA Breached" radius={[4,4,0,0]} />
                    </BarChart>
                  ) : (
                    <LineChart data={perTeamTrend} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} height={20} />
                      <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} width={28} domain={[0, 'dataMax + 2']} allowDecimals={false} />
                      <Tooltip contentStyle={TIP} />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
                      {perTeamPeriod.map((grp, i) => (
                        <Line key={grp.key} type="monotone" dataKey={grp.key}
                          stroke={[BLUE, CYAN, AMBER, GREEN, RED][i % 5]}
                          strokeWidth={2.5} dot={false} />
                      ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </div>
            </ChartCard>
          )}

          {/* By IT Service */}
          {visible.services && (() => {
            const limit = selectedKeys.length > 0 ? aggServices.length : 8
            const shown = aggServices.slice(0, limit)
            const shownTotal = shown.reduce((s, x) => s + x.count, 0)
            const other = periodTotals.total - shownTotal
            return (
              <ChartCard icon={Activity} iconGrad="from-[#00ACED] to-[#00ACED] text-white"
                title="By IT Service"
                subtitle={selectedKeys.length > 0 ? `${aggServices.length} services in period` : 'Top 8 by volume'}
              >
                <div className="space-y-2.5 mt-1">
                  {shown.map(({ service, count }) => (
                    <div key={service}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-slate-700 truncate max-w-[200px]">{service}</span>
                        <span className="text-xs font-bold text-slate-600 ml-2 flex-shrink-0">{count}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100">
                        <div className="h-1.5 rounded-full" style={{ width: `${(count / maxService) * 100}%`, background: BLUE }} />
                      </div>
                    </div>
                  ))}
                  {other > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-slate-400 italic">Unassigned / other</span>
                        <span className="text-xs font-bold text-slate-400 ml-2 flex-shrink-0">{other}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100">
                        <div className="h-1.5 rounded-full" style={{ width: `${(other / maxService) * 100}%`, background: '#cbd5e1' }} />
                      </div>
                    </div>
                  )}
                </div>
              </ChartCard>
            )
          })()}

          {/* SLA Breach Rate */}
          {visible.breach && (
            <ChartCard icon={TrendingDown} iconGrad="from-[#EF3340] to-[#EF3340] text-white"
              title="SLA Breach Rate" subtitle="Monthly trend"
            >
              <div style={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={breachRateTrend} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} height={20} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} width={32} unit="%" domain={[0, 'dataMax + 10']} allowDecimals={false} />
                    <Tooltip contentStyle={TIP} formatter={(v: any) => [`${v}%`, '']} />
                    {perTeamPeriod.length > 1 && <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />}
                    {perTeamPeriod.map((grp, i) => (
                      <Line key={grp.key} type="monotone" dataKey={grp.key}
                        stroke={[BLUE, CYAN, AMBER, GREEN, RED][i % 5]}
                        strokeWidth={2.5} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          )}

          {/* By Ticket Type */}
          {visible.types && (() => {
            const typeTotal = Object.values(aggTypes).reduce((a, v) => a + v, 0)
            const TYPE_COLOR: Record<string, string> = { Question: BLUE, Change: CYAN, Incident: RED }
            return (
              <ChartCard icon={TableProperties} iconGrad="from-slate-600 to-slate-700 text-white"
                title="By Ticket Type" subtitle="Breakdown in period"
              >
                <div className="space-y-3 mt-1">
                  {(['Question', 'Change', 'Incident'] as const).map(t => {
                    const count = aggTypes[t] ?? 0
                    const pct = typeTotal > 0 ? Math.round((count / typeTotal) * 100) : 0
                    return (
                      <div key={t}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-medium text-slate-700">{t}</span>
                          <span className="text-sm font-bold text-slate-800">{count} <span className="text-xs font-normal text-slate-400">{pct}%</span></span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100">
                          <div className="h-2 rounded-full" style={{ width: `${pct}%`, background: TYPE_COLOR[t] }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </ChartCard>
            )
          })()}

          {/* By Priority */}
          {visible.priority && aggPriority.length > 0 && (
            <ChartCard icon={AlertTriangle} iconGrad="from-[#F88806] to-[#F88806] text-white"
              title="Tickets by Priority" subtitle={`${aggPriority.reduce((a,[,c])=>a+c,0)} prioritised in period`}
            >
              <div className="space-y-3 mt-1">
                {aggPriority.map(([p, count]) => (
                  <div key={p} className="flex items-center gap-3">
                    <span className="text-xs font-bold text-slate-600 w-6">{p}</span>
                    <div className="flex-1 h-3 rounded-full bg-slate-100">
                      <div className="h-3 rounded-full" style={{ width: `${(count / maxPriority) * 100}%`, background: PRI_COLOR[p] ?? BLUE }} />
                    </div>
                    <span className="text-xs font-bold text-slate-700 w-5 text-right">{count}</span>
                    <span className="text-xs text-slate-400 w-16">{PRI_LABEL[p]}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span>Total</span>
                <span className="font-bold text-slate-700">{aggPriority.reduce((a, [, c]) => a + c, 0)}</span>
              </div>
            </ChartCard>
          )}

          {/* By Status */}
          {visible.status && Object.keys(aggByStatus).length > 0 && (() => {
            const STATUS_ORDER = ['Closed / Resolved', 'Active', 'Waiting', 'Closed Incomplete'] as const
            const STATUS_COLOR: Record<string, string> = {
              'Closed / Resolved': GREEN,
              'Active':            BLUE,
              'Waiting':           AMBER,
              'Closed Incomplete': '#F97316',
            }
            const TYPE_COLOR: Record<string, string> = { Question: BLUE, Change: CYAN, Incident: RED }
            const statusTotal = Object.values(aggByStatus).reduce((a, v) => a + v, 0)
            const maxStatus   = Math.max(...Object.values(aggByStatus), 1)
            return (
              <ChartCard icon={CheckCircle2} iconGrad="from-[#10b981] to-[#059669] text-white"
                title="By Status" subtitle="All-time ticket resolution snapshot"
              >
                {/* Compact type pills - period-filtered to match selected period */}
                <div className="flex gap-2 flex-wrap mb-4">
                  {(['Question', 'Change', 'Incident'] as const).map(t => {
                    const n = aggTypes[t] ?? 0
                    if (!n) return null
                    return (
                      <span key={t} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                        style={{ background: `${TYPE_COLOR[t]}15`, color: TYPE_COLOR[t] }}>
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: TYPE_COLOR[t] }} />
                        {t} · {n.toLocaleString()}
                      </span>
                    )
                  })}
                </div>
                <div className="border-t border-slate-100 pt-4 space-y-3">
                  {STATUS_ORDER.filter(s => aggByStatus[s] !== undefined).map(s => {
                    const count = aggByStatus[s] ?? 0
                    const pct   = statusTotal > 0 ? Math.round((count / statusTotal) * 100) : 0
                    return (
                      <div key={s}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-medium text-slate-700">{s}</span>
                          <span className="text-sm font-bold text-slate-800">{count.toLocaleString()} <span className="text-xs font-normal text-slate-400">{pct}%</span></span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100">
                          <div className="h-2 rounded-full" style={{ width: `${(count / maxStatus) * 100}%`, background: STATUS_COLOR[s] }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                  <span>Total</span>
                  <span className="font-bold text-slate-700">{statusTotal.toLocaleString()}</span>
                </div>
              </ChartCard>
            )
          })()}

          {/* Team Breakdown */}
          {visible.teams && selectedKeys.length <= 1 && (
            <div className="col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-8 flex items-center justify-center text-slate-400 text-sm">
              Select 2 or more assignment groups to see the team breakdown
            </div>
          )}
          {visible.teams && selectedKeys.length > 1 && (
            <div className="col-span-2 grid grid-cols-2 gap-4">
              {perTeamPeriod.map(({ key, label, color, total, breached, rate }) => (
                <div key={key} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                    <span className="text-sm font-bold text-slate-700">{label}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <div className="text-2xl font-bold text-slate-800">{total.toLocaleString()}</div>
                      <div className="text-xs text-slate-400 mt-0.5">tickets</div>
                    </div>
                    <div className="border-x border-slate-100">
                      <div className="text-2xl font-bold text-red-500">{breached}</div>
                      <div className="text-xs text-slate-400 mt-0.5">breaches</div>
                    </div>
                    <div>
                      <div className={`text-2xl font-bold ${rate > 30 ? 'text-red-500' : rate > 15 ? 'text-amber-500' : 'text-green-600'}`}>{rate}%</div>
                      <div className="text-xs text-slate-400 mt-0.5">breach rate</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      )}

      {/* Jira (Commercial AEM BAU) */}
      {jiraActive && (
        jiraSnapshot
          ? <JiraPanel snapshot={jiraSnapshot} filtered={jiraFiltered} filter={jiraFilter} onToggleLabel={jiraToggleLabel} />
          : (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 flex flex-col items-center justify-center text-center gap-3">
              <Database className="w-8 h-8 text-slate-300" />
              <div className="text-slate-600 font-semibold">Jira data not synced yet</div>
              <div className="text-slate-400 text-sm max-w-sm">
                The daily sync runs at 09:15 UTC. {isAdmin ? 'You can trigger it from Settings → Operational data sync.' : 'Check back shortly.'}
              </div>
            </div>
          )
      )}
    </div>
  )
}

function SlideFrame({ onExit, children }: { onExit: () => void; children: ReactNode }) {
  const [scale, setScale] = useState(1)
  const W = 1280, H = 720
  useEffect(() => {
    const fit = () => setScale(Math.min((window.innerWidth - 80) / W, (window.innerHeight - 80) / H))
    fit()
    window.addEventListener('resize', fit)
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onExit() }
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('resize', fit); window.removeEventListener('keydown', onKey) }
  }, [onExit])
  return (
    <div className="fixed inset-0 bg-slate-800 z-40 flex items-center justify-center">
      <div className="absolute top-4 right-4 z-10">
        <button onClick={onExit}
          className="px-3 py-1.5 rounded-lg bg-white text-slate-700 text-xs font-semibold hover:bg-slate-100">
          Exit (Esc)
        </button>
      </div>
      <div style={{ width: W, height: H, transform: `scale(${scale})`, transformOrigin: 'center center' }}
        className="bg-white shadow-2xl rounded-lg overflow-hidden">
        {children}
      </div>
    </div>
  )
}